import React, { useCallback, useEffect, useState } from "react";
import { Container, Fab, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add } from "@material-ui/icons";
import { useIntl } from "react-intl";

import FloatingActions from "../../ui/FloatingActions";
import Confirm from "../../ui/Confirm";

import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import { account, common } from "../../messages";

import { useQuestionsCache } from "./ManageQuiz"; // cache globale delle questions
import QuizList from "../../components/QuizList";
import QuizEditorDialog from "../../components/QuizEditorDialog";
import QuestionPickerDialog from "../../components/QuestionPickerDialog";

// normalizza testo per LIKE case-insensitive
function normalizeQuestionForSearch(q) {
  const base = (q?.text ?? q?.name ?? "") + " " + (q?.description ?? "");
  return base
    .toString()
    .trimStart()
    .replace(/^[^\p{L}\p{N}]+/u, "") // rimuovi punteggiatura iniziale (' " . ecc.)
    .toLowerCase();
}

// label della categoria: se nella mappa non esiste, mostra l'id grezzo
function getQuestionCategoryLabel(q, categoryMap) {
  if (!q?.category) return null;
  return categoryMap[q.category] || q.category;
}

const useStyles = makeStyles(() => ({
  fabWrap: { position: "relative", display: "inline-flex" },
  fabProgress: {
    position: "absolute",
    top: -6,
    right: -6,
    zIndex: 1,
  },
}));

export default function ManageQuizGroups() {
  const classes = useStyles();
  const intl = useIntl();
  const { bmapi, notifyError, notifySuccess, businessId } = useBmapi();

  // cache globale di tutte le domande
  const { questions, loadQuestionsOnce } = useQuestionsCache();

  // ===== QUIZ LIST STATE =====
  const [quizList, setQuizList] = useState([]);
  const [quizSearchText, setQuizSearchText] = useState("");

  // menu MoreVert del singolo quiz
  // { el: HTMLElement, quizId: string } | null
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // ===== CATEGORY MAP =====
  // mappa { categoryId: "label leggibile" }
  const [categoryMap, setCategoryMap] = useState({});

  // ===== DELETE CONFIRM =====
  // { quizId: string } | false
  const [deleteRequest, setDeleteRequest] = useState(false);

  // ===== EDITOR QUIZ STATE =====
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // "create" | "edit"
  const [editorQuizId, setEditorQuizId] = useState(null);
  const [editorName, setEditorName] = useState("");
  const [editorDesc, setEditorDesc] = useState("");
  const [editorQuestionIds, setEditorQuestionIds] = useState([]); // array ordinato di questionId
  const [editorNameError, setEditorNameError] = useState(false);

  // domande "espandibili" dentro l'editor quiz
  // Set(questionId)
  const [expandedSelectedQ, setExpandedSelectedQ] = useState(new Set());

  // conferma chiusura editor se dirty
  const [closeEditorConfirm, setCloseEditorConfirm] = useState(false);

  // ===== PICKER DOMANDE STATE =====
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFilterCategory, setPickerFilterCategory] = useState("");
  const [pickerSearchText, setPickerSearchText] = useState("");
  const [pickerExpandedQ, setPickerExpandedQ] = useState(new Set());
  const [pickerSelectedIds, setPickerSelectedIds] = useState([]); // questionIds selezionate (checkbox)

  // ===== API: LOAD QUIZ LIST =====
  const loadQuizList = useCallback(() => {
    if (!bmapi) return;
    bmapi
      .getQuizList({
        business: businessId,
        lang: bmapi.getUserInfo().business.default_language,
      })
      .then((resp) => {
        setQuizList(resp || []);
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, intl, notifyError, businessId]);

  useEffect(() => {
    loadQuizList();
  }, [loadQuizList]);

  // ===== API: LOAD CATEGORY MAP =====
  const loadCategoriesOnce = useCallback(async () => {
    if (!bmapi) return;
    try {
      const genList = await bmapi.getGenListAdmin(
        "quiz-categories",
        businessId
      );

      const items = genList?.items || [];
      const langs = genList?.all_lang || [];
      const defaultLang = bmapi.getUserInfo().business.default_language;
      const langIdx = langs.indexOf(defaultLang);

      const map = {};
      items.forEach((it) => {
        let label = it.item_id;
        if (
          Array.isArray(it.values) &&
          langIdx >= 0 &&
          it.values[langIdx]?.str_value !== undefined
        ) {
          label = it.values[langIdx].str_value;
        }
        map[it.item_id] = label;
      });

      setCategoryMap(map);
    } catch (err) {
      console.log("Failed to load categories:", err);
    }
  }, [bmapi, businessId]);

  // ===== MENU HANDLERS =====
  const handleOpenMenu = ({ el, quizId }) => {
    setMenuAnchorEl({ el, quizId });
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  // ===== EDITOR OPEN =====
  const openCreateEditor = async () => {
    await loadQuestionsOnce();
    await loadCategoriesOnce();

    setEditorMode("create");
    setEditorQuizId(null);
    setEditorName("");
    setEditorDesc("");
    setEditorQuestionIds([]);
    setEditorNameError(false);
    setEditorBusy(false);
    setEditorDirty(false);
    setExpandedSelectedQ(new Set());
    setEditorOpen(true);
  };

  const openEditEditor = async (quizId) => {
    await loadQuestionsOnce();
    await loadCategoriesOnce();

    const qz = quizList.find((q) => q.id === quizId);

    // TODO quando il backend ci darà le domande e il loro ordine:
    // const qIds = qz.questions?.map(q => q.id) ?? []
    const qIds = [];

    setEditorMode("edit");
    setEditorQuizId(quizId);
    setEditorName(qz?.name ?? "");
    setEditorDesc(qz?.description ?? "");
    setEditorQuestionIds(qIds);
    setEditorNameError(false);
    setEditorBusy(false);
    setEditorDirty(false);
    setExpandedSelectedQ(new Set());
    setEditorOpen(true);
  };

  // ===== EDITOR CLOSE =====
  const requestCloseEditor = () => {
    if (editorBusy) return;
    if (editorDirty) {
      setCloseEditorConfirm(true);
    } else {
      setEditorOpen(false);
    }
  };

  const confirmCloseEditor = () => {
    setCloseEditorConfirm(false);
    setEditorDirty(false);
    setEditorOpen(false);
  };

  // ===== EDITOR FIELD HANDLERS =====
  const handleEditorNameChange = (e) => {
    setEditorName(e.target.value);
    setEditorNameError(false);
    setEditorDirty(true);
  };

  const handleEditorDescChange = (e) => {
    setEditorDesc(e.target.value);
    setEditorDirty(true);
  };

  // ===== EDITOR QUESTION LIST OPS =====
  const handleRemoveQuestionFromQuiz = (qid) => {
    setEditorQuestionIds((prev) => prev.filter((id) => id !== qid));
    setExpandedSelectedQ((prev) => {
      const next = new Set(prev);
      next.delete(qid);
      return next;
    });
    setEditorDirty(true);
  };

  const handleMoveQuestionUp = (qid) => {
    setEditorQuestionIds((prev) => {
      const arr = [...prev];
      const idx = arr.indexOf(qid);
      if (idx > 0) {
        const tmp = arr[idx - 1];
        arr[idx - 1] = arr[idx];
        arr[idx] = tmp;
      }
      return arr;
    });
    setEditorDirty(true);
  };

  const handleMoveQuestionDown = (qid) => {
    setEditorQuestionIds((prev) => {
      const arr = [...prev];
      const idx = arr.indexOf(qid);
      if (idx >= 0 && idx < arr.length - 1) {
        const tmp = arr[idx + 1];
        arr[idx + 1] = arr[idx];
        arr[idx] = tmp;
      }
      return arr;
    });
    setEditorDirty(true);
  };

  const toggleExpandedSelectedQ = (qid) => {
    setExpandedSelectedQ((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  };

  // ===== EDITOR SAVE =====
  const validateEditor = () => {
    const invalidName = !editorName.trim();
    setEditorNameError(invalidName);
    return !invalidName;
  };

  const handleSaveEditor = async () => {
    if (!bmapi) return;
    if (!validateEditor()) return;

    setEditorBusy(true);
    try {
      const payload = {
        name: editorName.trim(),
        description: editorDesc?.trim() ?? "",
        // questionIds: editorQuestionIds // da inviare quando supportato
      };

      if (editorMode === "edit" && editorQuizId) {
        await bmapi.updateCourse(editorQuizId, payload);
      } else {
        await bmapi.createCourse(payload);
      }

      notifySuccess(intl.formatMessage(account.saveConfirm));
      setEditorBusy(false);
      setEditorDirty(false);
      setEditorOpen(false);
      loadQuizList();
    } catch (e) {
      setEditorBusy(false);
      notifyError(getErrorMessageString(e, intl));
    }
  };

  // ===== DELETE QUIZ =====
  const handleDeleteQuiz = async () => {
    if (!deleteRequest || !deleteRequest.quizId) return;
    try {
      await bmapi.deleteCourse(deleteRequest.quizId);
      notifySuccess(intl.formatMessage(common.deleteElement));
      setDeleteRequest(false);
      loadQuizList();
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    }
  };

  // ===== PICKER =====
  const openPicker = () => {
    setPickerFilterCategory("");
    setPickerSearchText("");
    setPickerSelectedIds([]);
    setPickerExpandedQ(new Set());
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
  };

  const togglePickerCheck = (qid, checked) => {
    setPickerSelectedIds((prev) => {
      if (checked) {
        if (prev.includes(qid)) return prev;
        return [...prev, qid];
      }
      return prev.filter((id) => id !== qid);
    });
  };

  const togglePickerExpandQ = (qid) => {
    setPickerExpandedQ((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  };

  const confirmAddSelectedFromPicker = () => {
    setEditorQuestionIds((prev) => {
      const merged = [...prev];
      pickerSelectedIds.forEach((qid) => {
        if (!merged.includes(qid)) merged.push(qid);
      });
      return merged;
    });
    setEditorDirty(true);
    setPickerOpen(false);
  };

  // ===== DERIVED VIEW DATA =====

  // lista quiz visibile (filtrata e ordinata)
  const visibleQuizList = quizList
    .filter((item) => {
      const needle = quizSearchText.trim().toLowerCase();
      if (!needle) return true;
      const blob = `${item?.name ?? ""} ${item?.description ?? ""}`
        .toLowerCase()
        .trim();
      return blob.includes(needle);
    })
    .slice()
    .sort((a, b) =>
      (a?.name ?? "").localeCompare(b?.name ?? "", undefined, {
        sensitivity: "base",
      })
    );

  // domande mostrate nell'editor quiz, mantenendo l'ordine editorQuestionIds
  const editorQuestions = editorQuestionIds
    .map((qid) => questions.find((q) => q.id === qid))
    .filter(Boolean)
    .map((q) => ({
      ...q,
      categoryLabel: getQuestionCategoryLabel(q, categoryMap),
      expanded: expandedSelectedQ.has(q.id),
    }));

  // domande mostrate nel picker
  const filteredPickerQuestions = questions
    .filter((q) => {
      if (pickerFilterCategory && q.category !== pickerFilterCategory) {
        return false;
      }
      if (pickerSearchText.trim()) {
        const hay = normalizeQuestionForSearch(q);
        if (!hay.includes(pickerSearchText.trim().toLowerCase())) {
          return false;
        }
      }
      return true;
    })
    .slice()
    .sort((a, b) =>
      (a?.text ?? a?.name ?? "").localeCompare(
        b?.text ?? b?.name ?? "",
        undefined,
        { sensitivity: "base" }
      )
    )
    .map((q) => ({
      ...q,
      categoryLabel: getQuestionCategoryLabel(q, categoryMap),
      expanded: pickerExpandedQ.has(q.id),
      checked: pickerSelectedIds.includes(q.id),
      disabled: editorQuestionIds.includes(q.id),
    }));

  return (
    <Container disableGutters>
      {/* LISTA QUIZ + MENU */}
      <QuizList
        list={visibleQuizList}
        searchText={quizSearchText}
        onSearchChange={(val) => setQuizSearchText(val)}
        menuAnchorEl={menuAnchorEl}
        onOpenMenu={(payload) => {
          // payload = { el, quizId }
          handleOpenMenu(payload);
        }}
        onCloseMenu={handleCloseMenu}
        onEditClick={async (quizId) => {
          await openEditEditor(quizId);
        }}
        onDeleteClick={(quizId) => {
          setDeleteRequest({ quizId });
        }}
      />

      {/* DIALOG EDITOR QUIZ */}
      <QuizEditorDialog
        open={editorOpen}
        busy={editorBusy}
        mode={editorMode}
        name={editorName}
        desc={editorDesc}
        nameError={editorNameError}
        questions={editorQuestions}
        onChangeName={handleEditorNameChange}
        onChangeDesc={handleEditorDescChange}
        onClose={requestCloseEditor}
        onConfirm={handleSaveEditor}
        onToggleExpandQuestion={(qid) => toggleExpandedSelectedQ(qid)}
        onMoveUp={(qid) => handleMoveQuestionUp(qid)}
        onMoveDown={(qid) => handleMoveQuestionDown(qid)}
        onRemove={(qid) => handleRemoveQuestionFromQuiz(qid)}
        onAddQuestionClick={openPicker}
      />

      {/* DIALOG PICKER DOMANDE */}
      <QuestionPickerDialog
        open={pickerOpen}
        questions={filteredPickerQuestions}
        filterCategory={pickerFilterCategory}
        searchText={pickerSearchText}
        onChangeCategory={(val) => setPickerFilterCategory(val)}
        onChangeSearch={(val) => setPickerSearchText(val)}
        onToggleExpandQuestion={(qid) => togglePickerExpandQ(qid)}
        onToggleCheck={(qid, checked) => togglePickerCheck(qid, checked)}
        onConfirm={confirmAddSelectedFromPicker}
        onClose={closePicker}
      />

      {/* CONFERME */}
      <Confirm
        open={!!closeEditorConfirm}
        onConfirm={confirmCloseEditor}
        onCancel={() => setCloseEditorConfirm(false)}
        text="Hai modifiche non salvate. Uscire comunque?"
      />
      <Confirm
        open={!!deleteRequest}
        onConfirm={handleDeleteQuiz}
        onCancel={() => setDeleteRequest(false)}
        text="Confermi l'eliminazione?"
      />

      {/* FAB nuovo quiz */}
      <FloatingActions>
        <span className={classes.fabWrap}>
          <Fab
            variant="extended"
            color="primary"
            onClick={openCreateEditor}
            disabled={editorBusy}
          >
            <Add />
            Nuovo quiz
          </Fab>
          {editorBusy && (
            <CircularProgress size={48} className={classes.fabProgress} />
          )}
        </span>
      </FloatingActions>
    </Container>
  );
}
